// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useTranslation } from "react-i18next";
import type { TaskDefinitionDetailResult } from "@/api/hooks/use-tasks";
import { ChainLink } from "@/components/shared/chain-link";
import { DateTimeValue } from "@/components/shared/datetime-value";
import { ReadOnlySwitch } from "@/components/shared/read-only-switch";
import { StoreLink } from "@/components/shared/store-link";
import { UserLink } from "@/components/shared/user-link";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { TaskCardPreview } from "@/features/tasks/task-card-preview/task-card-preview";
import { isInlineSvgIcon, TaskIcon } from "@/features/tasks/task-icon";

interface TaskInfoTabProps {
  task: TaskDefinitionDetailResult;
}

export function TaskInfoTab({ task }: TaskInfoTabProps) {
  const { t } = useTranslation("back-office");
  const settings = task.settings;

  // A built-in type has no definition or appearance row of its own — the API composes its card
  // from the Action Tool's hard-coded design and the values `action_task_settings` does store, so
  // the preview is the same, but the fields around it describe stores rather than a definition.
  if (task.kind === "builtIn") {
    return <BuiltInTaskInfo task={task} />;
  }
  if (!settings) return null;

  return (
    <div className="flex flex-col gap-8 p-6 lg:flex-row">
      <div className="max-w-2xl flex-1 space-y-6">
        <div className="grid grid-cols-[190px_1fr] gap-y-3 text-sm">
          <span className="text-muted-foreground">{t("tasks.fields.subtype")}</span>
          <span className="font-mono">{task.customTaskSubtype}</span>

          <span className="text-muted-foreground">{t("tasks.fields.fullName")}</span>
          <span>{settings.fullName}</span>

          <span className="text-muted-foreground">{t("tasks.fields.shortName")}</span>
          <span>{settings.shortName || <span className="text-muted-foreground">—</span>}</span>

          <span className="text-muted-foreground">{t("tasks.fields.acronym")}</span>
          <span>
            {settings.acronym ? (
              <Badge variant="outline" className="font-mono font-normal">
                {settings.acronym}
              </Badge>
            ) : (
              <span className="text-muted-foreground">—</span>
            )}
          </span>

          <Separator className="col-span-2" />

          <span className="self-center text-muted-foreground">{t("tasks.fields.icon")}</span>
          <span className="flex items-center gap-2">
            <span
              className="flex h-7 w-7 items-center justify-center rounded"
              style={{ backgroundColor: settings.color }}
            >
              <TaskIcon icon={settings.icon} color={settings.headerFontColor} className="h-5 w-5" />
            </span>
            <span className="font-mono text-xs text-muted-foreground">
              {isInlineSvgIcon(settings.icon)
                ? t("tasks.fields.inlineSvgIcon")
                : settings.icon || t("tasks.fields.noIcon")}
            </span>
          </span>

          <span className="self-center text-muted-foreground">{t("tasks.fields.color")}</span>
          <span className="flex items-center gap-2">
            <span className="h-4 w-4 rounded border" style={{ backgroundColor: settings.color }} />
            <span className="font-mono text-xs">{settings.color}</span>
          </span>

          <span className="self-center text-muted-foreground">{t("tasks.fields.subHeaderColor")}</span>
          <span className="flex items-center gap-2">
            {settings.subHeaderColor ? (
              <>
                <span className="h-4 w-4 rounded border" style={{ backgroundColor: settings.subHeaderColor }} />
                <span className="font-mono text-xs">{settings.subHeaderColor}</span>
              </>
            ) : (
              <span className="text-muted-foreground">—</span>
            )}
          </span>

          <span className="text-muted-foreground">{t("tasks.fields.headerMode")}</span>
          <span className="font-mono text-xs">{settings.headerMode}</span>

          <span className="text-muted-foreground">{t("tasks.fields.subHeaderMode")}</span>
          <span className="font-mono text-xs">
            {settings.subHeaderMode || <span className="font-sans text-muted-foreground">—</span>}
          </span>

          <Separator className="col-span-2" />

          <span className="self-center text-muted-foreground">{t("tasks.fields.pickable")}</span>
          <span>
            {/* Reflects the stored value; the Edit tab is where it changes */}
            <ReadOnlySwitch checked={settings.isPickable} />
          </span>

          <span className="text-muted-foreground">{t("tasks.fields.algoFillAction")}</span>
          <span className="font-mono text-xs">{settings.algoFillAction}</span>

          <span className="self-center text-muted-foreground">{t("tasks.fields.serviceUser")}</span>
          <span>
            {task.serviceUserPublicId ? (
              <UserLink
                publicId={task.serviceUserPublicId}
                legibleName={task.serviceUserName ?? task.serviceUserPublicId}
              />
            ) : (
              <span className="text-muted-foreground">{settings.serviceUserId ?? "—"}</span>
            )}
          </span>

          <Separator className="col-span-2" />

          <span className="text-muted-foreground">{t("tasks.fields.createdAt")}</span>
          <span>
            <DateTimeValue value={task.createdAt} />
          </span>

          <span className="text-muted-foreground">{t("tasks.fields.updatedAt")}</span>
          <span>
            <DateTimeValue value={task.updatedAt} />
          </span>
        </div>

        <div className="space-y-2">
          <h3 className="text-sm font-medium">{t("tasks.info.overridesTitle")}</h3>
          <p className="text-sm text-muted-foreground">{t("tasks.info.overridesHint")}</p>
          {task.scopes.length ? (
            <ul className="space-y-1.5">
              {task.scopes.map((scope) => (
                <li
                  key={`${scope.chainId ?? "-"}:${scope.storeId ?? "-"}`}
                  className="flex flex-wrap items-center gap-2 text-sm"
                >
                  {scope.storeName ? (
                    <StoreLink storeName={scope.storeName} legibleName={scope.storeLegibleName ?? scope.storeName} />
                  ) : scope.chainName ? (
                    <ChainLink chainName={scope.chainName} legibleName={scope.chainLegibleName ?? scope.chainName} />
                  ) : (
                    <span className="text-muted-foreground">{t("tasks.scope.global")}</span>
                  )}
                  {scope.hasDefinitionOverride && (
                    <Badge variant="outline" className="font-normal">
                      {t("tasks.info.definitionOverride")}
                    </Badge>
                  )}
                  {scope.hasAppearanceOverride && (
                    <Badge variant="outline" className="font-normal">
                      {t("tasks.info.appearanceOverride")}
                    </Badge>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground italic">{t("tasks.info.noOverrides")}</p>
          )}
        </div>
      </div>

      <div className="shrink-0">
        <TaskCardPreview settings={settings} />
      </div>
    </div>
  );
}

/** Info for a built-in task type: what its per-store `action_task_settings` rows amount to. */
function BuiltInTaskInfo({ task }: TaskInfoTabProps) {
  const { t } = useTranslation("back-office");
  const builtIn = task.builtIn;
  if (!builtIn) return null;
  const icon = builtIn.icon;

  return (
    <div className="flex flex-col gap-8 p-6 lg:flex-row">
      <div className="max-w-2xl flex-1 space-y-6">
        <div className="grid grid-cols-[190px_1fr] gap-y-3 text-sm">
          <span className="text-muted-foreground">{t("tasks.fields.type")}</span>
          <span className="font-mono">{builtIn.type}</span>

          <span className="text-muted-foreground">{t("tasks.fields.fullName")}</span>
          <span>{builtIn.name}</span>

          <Separator className="col-span-2" />

          <span className="self-center text-muted-foreground">{t("tasks.fields.icon")}</span>
          <span className="flex items-center gap-2">
            <span
              className="flex h-7 w-7 items-center justify-center rounded"
              style={{ backgroundColor: builtIn.color }}
            >
              <TaskIcon icon={icon} color="#ffffff" className="h-5 w-5" />
            </span>
            {/* Always a name for a built-in type — either a Material icon or one of the Action
                Tool's own glyphs — never the markup a custom appearance may store */}
            <span className="font-mono text-xs text-muted-foreground">{icon}</span>
          </span>

          <span className="self-center text-muted-foreground">{t("tasks.fields.color")}</span>
          <span className="flex items-center gap-2">
            <span className="h-4 w-4 rounded border" style={{ backgroundColor: builtIn.color }} />
            <span className="font-mono text-xs">{builtIn.color}</span>
          </span>

          <Separator className="col-span-2" />

          <span className="self-center text-muted-foreground">{t("tasks.fields.stores")}</span>
          <span className="flex flex-wrap gap-1.5">
            <Badge variant="secondary">
              {t("tasks.fields.activeStores", { active: builtIn.activeSettingsCount, total: builtIn.settingsCount })}
            </Badge>
            <Badge variant="secondary">
              {t("tasks.fields.defaultStores", { count: builtIn.defaultSettingsCount })}
            </Badge>
          </span>

          <span className="self-center text-muted-foreground">{t("tasks.fields.actionCounts")}</span>
          <span className="flex flex-wrap gap-1.5">
            <Badge variant="secondary">{t("tasks.fields.workedCount", { count: builtIn.workedActionCount })}</Badge>
            <Badge variant="secondary">
              {t("tasks.fields.notWorkedCount", { count: builtIn.notWorkedActionCount })}
            </Badge>
            <Badge variant="secondary">{t("tasks.fields.snoozeCount", { count: builtIn.snoozeOptionCount })}</Badge>
          </span>

          <span className="text-muted-foreground">{t("tasks.fields.updatedAt")}</span>
          <span>
            <DateTimeValue value={builtIn.updatedAt} />
          </span>
        </div>
      </div>

      {task.settings && (
        <div className="shrink-0">
          <TaskCardPreview settings={task.settings} />
        </div>
      )}
    </div>
  );
}
